<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { usePremiumStore } from '@stores/premium'
import { useAnalytics } from '@composables/use-analytics'
import bannerTracker from '@services/mixpanel/banner-tracker'

// A banner counts as "seen" when at least half of it is in the viewport.
const SEEN_VISIBILITY_THRESHOLD = 0.5

const props = defineProps({
    source: {
        type: String,
        required: true,
    },
})

const premiumStore = usePremiumStore()
const router = useRouter()
const analyticsService = useAnalytics()

const root = ref(null)

let observer = null
let seenTracked = false

const isPremium = computed(() => premiumStore.isPremium)

const showBanner = computed(() => !isPremium.value)

function onGoPremium() {
    analyticsService.premiumUpsellInitiated(props.source)
    bannerTracker.onBannerTapped(props.source)
    router.push({ name: 'premium/view-plans', query: { from: props.source } })
}

function trackSeen() {
    if (seenTracked) return
    seenTracked = true
    bannerTracker.onBannerSeen(props.source)
}

function disconnectObserver() {
    if (observer) {
        observer.disconnect()
        observer = null
    }
}

function observeSeen() {
    if (seenTracked || observer || !showBanner.value) return

    if (!window.IntersectionObserver) {
        // No observer support: count the render as a seen (LazyLoad precedent)
        trackSeen()
        return
    }

    observer = new IntersectionObserver(
        entries => {
            if (entries.some(entry => entry.intersectionRatio >= SEEN_VISIBILITY_THRESHOLD)) {
                trackSeen()
                disconnectObserver()
            }
        },
        { threshold: SEEN_VISIBILITY_THRESHOLD }
    )
    observer.observe(root.value)
}

// The root is v-if'd on showBanner, and inside a pending Suspense (an async layout)
// Vue sets the ref only once it resolves, so observe when the element arrives.
watch(root, element => {
    if (element) observeSeen()
})

onBeforeUnmount(disconnectObserver)
</script>

<template>
    <div
        v-if="showBanner"
        ref="root"
        class="premium-banner"
    >
        <div class="content">
            <BaseIcon
                name="headphones"
                :size="32"
                class="icon"
            />
            <span class="headline">HI-FI AUDIO. NO LIMITS.</span>
        </div>
        <div class="cta">
            <BaseButton
                variant="primary"
                size="xl"
                @click="onGoPremium"
            >Go Premium</BaseButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// Brand banner creative — Figma 816:361 (desktop) / 816:426 (mobile). The icon
// size, headline size, banner padding and gradient angle are pixel-specific
// brand values with no matching design token; kept as literals so the banner
// matches the Figma exactly across the mobile / tablet / desktop tiers.
$banner-glow: rgba($c-accent-pressed, 0.57);
$banner-shade: rgba($c-black, 0.57);
// stylelint-disable-next-line color-no-hex -- brand literal (see above), no matching token
$banner-base: #3f3f3f; // the banner keeps its visual weight (WDS PRD DoD)

.premium-banner {
    display: flex;
    flex-direction: column;
    gap: $s-m;
    align-items: center;
    padding: $s-xl;
    background: linear-gradient(97.08deg, $banner-glow 0%, $banner-shade 38%), $banner-base;
    border-radius: $size-radius-l;

    @include tablet {
        flex-direction: row;
        gap: $s-l;
        align-items: center;
        justify-content: space-between;
        padding: $s-xl $s-xxl;
        background: linear-gradient(106.24deg, $banner-glow 0%, $banner-shade 38%), $banner-base;
    }

    @include desktop {
        padding: 40px 32px;
    }
}

.content {
    display: flex;
    gap: $s-l;
    align-items: center;
}

.icon {
    display: none;
    color: $c-white;

    @include tablet {
        display: block;
        width: 46px;
        height: 46px;
    }

    @include desktop {
        width: 60px;
        height: 60px;
    }
}

.headline {
    font-size: 20px;
    font-weight: 800;
    line-height: 1.1;
    color: $c-white;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: -0.6px;

    @include tablet {
        font-size: 28px;
        text-align: left;
        letter-spacing: -0.84px;
    }

    @include desktop {
        font-size: 37px;
        letter-spacing: -1.11px;
    }
}

// Sizing lives in BaseButton's `xl` size; only the banner's own flex
// behaviour belongs here.
.cta {
    flex-shrink: 0;
}
</style>
